import { BehaviorSubject } from 'rxjs';
import { UserRole } from '../../../services/auth/auth.role';

export type AuthMockMode = 'logged-out' | 'logged-in-user' | 'logged-in-organization-admin' | 'logged-in-organization-representative' | 'logged-in-dataspace-authority';

const STORAGE_KEY = 'mock-auth-mode';

// Load initial mode from localStorage
const getInitialMode = (): AuthMockMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'logged-in-user' || stored === 'logged-in-organization-admin' || stored === 'logged-in-organization-representative' || stored === 'logged-in-dataspace-authority') {
    return stored;
  }
  return 'logged-out';
};

const authMockModeSubject = new BehaviorSubject<AuthMockMode>(getInitialMode());

export function setAuthMockMode(mode: AuthMockMode): void {
  authMockModeSubject.next(mode);
  localStorage.setItem(STORAGE_KEY, mode);
  console.log(`Auth mock mode set to: ${mode}`);
}

export function getAuthMockMode$() {
  return authMockModeSubject.asObservable();
}

export function getCurrentAuthMockMode(): AuthMockMode {
  return authMockModeSubject.value;
}

export function getMockedAuthToken(): string | null {
  const mode = getCurrentAuthMockMode();

  if (mode === 'logged-out') {
    return null;
  }

  // Create a mock JWT token structure
  const header = { alg: 'HS256', typ: 'JWT' };
  const payload = {
    sub: 'mock-user-id',
    name: 'John Smith',
    given_name: 'John',
    family_name: 'Smith',
    email: 'john.smith@example.com',
    preferred_username: 'john.smith',
    realm_access: {
      roles: mode === 'logged-in-organization-admin' ? ['organization_admin'] :
             mode === 'logged-in-organization-representative' ? ['organization_representative'] :
             mode === 'logged-in-dataspace-authority' ? ['dataspace_authority'] : ['organization_user']
    },
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 3600
  };

  // Base64 encode (simplified mock - not cryptographically secure)
  const encodedHeader = btoa(JSON.stringify(header));
  const encodedPayload = btoa(JSON.stringify(payload));
  const signature = 'mock-signature';

  return `${encodedHeader}.${encodedPayload}.${signature}`;
}

export function isLoggedIn(): boolean {
  return getCurrentAuthMockMode() !== 'logged-out';
}

export function getMockedUserId(): string {
  return 'mock-user-id';
}

export function getMockedDisplayName(): string {
  const mode = getCurrentAuthMockMode();
  if (mode === 'logged-out') return '';
  return 'John Smith';
}

export function getMockedUserRoles(): string[] {
  const mode = getCurrentAuthMockMode();

  if (mode === 'logged-out') {
    return [];
  }

  if (mode === 'logged-in-organization-admin') {
    return [UserRole.OrganizationUser, UserRole.OrganizationAdmin];
  }

  if (mode === 'logged-in-organization-representative') {
    return [UserRole.OrganizationUser, UserRole.OrganizationRepresentative];
  }

  if (mode === 'logged-in-dataspace-authority') {
    return [UserRole.OrganizationUser, UserRole.DataspaceAuthority];
  }

  return [UserRole.OrganizationUser];
}
